// Estilos do compositor de mensagens (M5.3b) da modernização do InfoChat.
//
// Função pura — sem JSX, sem I/O, sem leitura de localStorage/backend.
// Recebe o `theme` do MUI (idealmente já com `theme.infoChat` da Entrega
// M2) e devolve só objetos de estilo simples (CSS-in-JS), para serem
// mesclados nas classes/estilos inline já existentes de `MessageInput`.
// Não aplica nada sozinha — quem decide onde/como usar é esse arquivo.
//
// Helper independente das demais entregas (M5.1/M5.2/M5.3a) — por decisão
// explícita, não importa nem se acopla a elas, mesmo definindo um
// `focusRingAccent` equivalente (duplicação intencional, cada entrega fica
// isolada).
//
// Escopo desta entrega (M5.3b): superfície/borda/texto do compositor,
// cores institucionais dos controles com estado (assinatura, disparar
// fluxo, mensagem privada), semântica de alerta/saudável para
// cancelar/enviar áudio, semântica de "aguardando" para o composer de
// ticket pendente, semântica de "conectando" (informativo) para o aviso de
// ticket aguardando, cores do menu de anexos, e o anel de foco. Nenhuma cor
// de bolha de mensagem, citação ou amarelo de mensagem privada é decidida
// aqui — essas pertencem à M5.3a/decisões já protegidas, e o amarelo do
// próprio compositor (`messageInputWrapperPrivate`) permanece intocado por
// decisão explícita desta entrega também.
//
// Usa exclusivamente os tokens de `theme.infoChat` para cor — nunca um hex
// literal novo aqui.

import { createInfoChatDesignTokens } from "../theme/infoChatDesignTokens";

const hasCompleteInfoChatTokens = tokens =>
  Boolean(
    tokens &&
      tokens.brand &&
      tokens.surfaces &&
      tokens.borders &&
      tokens.text &&
      tokens.shadows &&
      tokens.transitions &&
      tokens.radii &&
      tokens.spacing &&
      tokens.status
  );

// Mesmo padrão de fallback seguro já usado nas entregas anteriores — nunca
// deixa o compositor quebrar caso theme.infoChat esteja ausente/incompleto,
// e nunca diverge dos tokens reais por reaproveitar a mesma função pura da
// M2.
const resolveInfoChatTokens = theme => {
  if (hasCompleteInfoChatTokens(theme?.infoChat)) {
    return theme.infoChat;
  }
  return createInfoChatDesignTokens(theme?.palette?.type, theme?.palette?.primary?.main);
};

/**
 * Estilos do compositor de mensagens modernizado (M5.3b).
 *
 * @param {object} theme - tema do MUI (idealmente já com `theme.infoChat`).
 */
export const createInfoChatMessageInputStyles = theme => {
  const tokens = resolveInfoChatTokens(theme);

  return {
    // ---- Superfície/borda do compositor ----

    // Fundo do wrapper principal e do aviso de arraste — antes "#eee"
    // fixo, sem reagir ao modo escuro.
    composerSurface: {
      backgroundColor: tokens.surfaces.base
    },
    // Borda superior do wrapper principal — antes "rgba(0, 0, 0, 0.12)"
    // fixo.
    composerBorder: {
      borderColor: tokens.borders.subtle
    },

    // ---- Ícones utilitários neutros (emoji, mic, enviar, encaminhar,
    // agendar, botão "+" de anexar) — antes `grey[700]`/"grey" fixos, sem
    // reagir ao modo escuro. Não são cores institucionais: são ícones de
    // utilidade neutra, mesmo papel do texto secundário do sistema de
    // tokens. ----
    neutralIconColor: {
      color: tokens.text.secondary
    },

    // ---- Controles com estado (assinatura, disparar fluxo, mensagem
    // privada) — antes `theme.mode==="light"?theme.palette.primary.main
    // :"#EEE"`, mesmo padrão problemático já corrigido em M5.1/M5.2: no
    // modo escuro perdia toda cor própria. Cor institucional única, sem
    // ramificar por modo, para o estado ATIVO desses três controles. ----
    actionIconColor: {
      color: tokens.brand.primaryColor
    },

    // ---- Cancelar/enviar áudio — antes "red"/"green" literais. Reaproveita
    // os tokens semânticos de status (alert=destrutivo, healthy=positivo) já
    // usados nos botões de ação da M5.1/M5.2, em vez de inventar cor nova. Os
    // indicadores de carregamento do áudio (circleLoading/audioLoading, antes
    // green[500]) reaproveitam a mesma cor "positiva". ----
    destructiveIconColor: {
      color: tokens.status.alert.main
    },
    positiveIconColor: {
      color: tokens.status.healthy.main
    },

    // ---- Composer de ticket pendente — antes "#FFE0B2"/"#FF9800"/"#E65100"
    // fixos. Reaproveita o token semântico "awaitingScan" (aguardando) já
    // usado no indicador de espera da lista de tickets (M5.1) e no toggle
    // mobile de status pendente. ----
    pendingComposerSurface: {
      backgroundColor: tokens.status.awaitingScan.surface
    },
    pendingComposerBorder: {
      borderColor: tokens.status.awaitingScan.main
    },
    pendingComposerText: {
      color: tokens.status.awaitingScan.main
    },

    // ---- Aviso informativo "Ticket Aguardando" — antes
    // "#E3F2FD"/"#2196F3"/"#1976D2" fixos. Reaproveita o token semântico
    // "connecting" (informativo/azul) já usado no botão "visualizar" e no
    // ícone de visualização da lista de tickets (M5.1/M5.2). ----
    infoBannerSurface: {
      backgroundColor: tokens.status.connecting.surface
    },
    infoBannerBorder: {
      borderColor: tokens.status.connecting.main
    },
    infoBannerText: {
      color: tokens.status.connecting.main
    },

    // ---- Menu de anexos (5 tipos) — antes um arco-íris de cores hardcoded
    // sem token (blue[800]/blue[500]/green[500]/"#7f66ff"/pink[500]).
    // Mapeados para os 4 tokens de status da M2 + a cor institucional,
    // priorizando manter os cinco tipos visualmente distintos entre si
    // (mesmo raciocínio já usado nos botões de ação da lista de tickets
    // M5.1) — não é uma alegação de que cada tipo tem um "status", só que
    // cada um usa uma fonte de cor diferente do sistema de tokens.
    // Mídia (imagem/vídeo) mantém a família azul do original;
    // reunião/vídeo-chamada mantém a família verde do original; os demais
    // (câmera/documento/contato) usam as fontes de cor restantes do
    // sistema para continuar distintos entre si, sem inventar hex novo. ----
    attachMediaColor: {
      color: tokens.status.connecting.main
    },
    attachCameraColor: {
      color: tokens.status.alert.main
    },
    attachDocColor: {
      color: tokens.status.awaitingScan.main
    },
    attachContactColor: {
      color: tokens.brand.primaryColor
    },
    attachMeetColor: {
      color: tokens.status.healthy.main
    },

    // ---- Resposta rápida selecionada (navegação por teclado no "/") —
    // antes três fontes concorrentes para a mesma decisão visual:
    // "#e3f2fd"/"#1e3a5f" fixos (sem token) em `getQuickAnswerItemStyle`,
    // `theme.palette.primary.light + "20"` num spread de override só na
    // árvore de ticket pendente/privado, e `theme.palette.primary.light +
    // "30"` na classe `quickAnswerItemSelected` (nunca visível de fato, já
    // que o `style` inline sempre vence sobre a classe) — três valores
    // diferentes disputando a mesma bolinha. Única superfície/borda agora,
    // consumida da mesma forma pelas duas árvores (desktop e
    // privado/pendente). Mesmo padrão de alpha hex já usado nas entregas
    // anteriores para o estado "selecionado" (M5.1).
    quickAnswerSelectedSurface: {
      backgroundColor: `${tokens.brand.primaryColor}1F`
    },
    quickAnswerSelectedBorder: {
      borderColor: tokens.brand.primaryColor
    },

    // ---- Banner "Fluxo em Execução" — antes estilos inline
    // "#E8F5E8"/"#4CAF50" sobre a classe `pendingAlert` (que já define um
    // `color` azul/informativo próprio, indevidamente herdado por este
    // banner que não tem nada de "informativo", e sim de "em andamento com
    // sucesso"). Reaproveita o token semântico "healthy" (mesma família já
    // usada em enviar áudio e no botão "aceitar"/"criar" da lista de
    // tickets), com superfície, borda E texto próprios — nunca herda o
    // texto azul do aviso informativo. ----
    successBannerSurface: {
      backgroundColor: tokens.status.healthy.surface
    },
    successBannerBorder: {
      borderColor: tokens.status.healthy.main
    },
    successBannerText: {
      color: tokens.status.healthy.main
    },

    // ---- Estado desabilitado dos controles do compositor ----
    controlDisabledColor: {
      color: tokens.text.disabled
    },

    // ---- Hover sutil dos controles do compositor — mesmo padrão de alpha
    // hex já usado nas entregas anteriores. ----
    controlHoverSurface: {
      backgroundColor: `${tokens.brand.primaryColor}0D`
    },

    // Anel de foco visível (navegação real por teclado) — mesma cor
    // institucional, mesmo raciocínio já corrigido em M4/M5.1/M5.2/M5.3a:
    // usa `outline`/`outlineOffset`, nunca `boxShadow` ou `backgroundColor`,
    // para nunca disputar a mesma propriedade usada pelos estados de
    // hover/superfície acima — o foco sempre aparece por cima de qualquer
    // estado, sem sobrescrever nada.
    focusRingAccent: {
      outline: `2px solid ${tokens.brand.primaryColor}`,
      outlineOffset: "-2px"
    }
  };
};

export default createInfoChatMessageInputStyles;
